import { queryOptions, useMutation, useQuery } from '@tanstack/react-query'

import { createQueryKeys, queryClient, type ApiError } from '../core'
import type { CreateTemplateVersionResponseDto, GenerateVariationBody, TemplateVersionDto, TemplateVersionFilters, TemplateVersionUpdateBody } from './template-versions.schemas'
import { templateVersionsService } from './template-versions.service'

export const templateVersionsKeys = createQueryKeys('template-versions')

export const templateVersionsQueries = {
  ofProject: (projectId: string, filters: Omit<TemplateVersionFilters, 'projectId'> = {}) =>
    queryOptions({ queryKey: templateVersionsKeys.all({ projectId, ...filters }), queryFn: ({ signal }) => templateVersionsService.ofProject(projectId, filters, signal) }),
}

/** `useTemplateVersions(projectId)`: the project's template variations, newest first. Asked for once and kept. Pass `undefined` to hold off. */
export function useTemplateVersions(projectId: string | undefined, filters: Omit<TemplateVersionFilters, 'projectId'> = {}) {
  return useQuery({ ...templateVersionsQueries.ofProject(projectId ?? '', filters), enabled: Boolean(projectId), staleTime: Infinity })
}

/** Asks the server again, now: called when the task that draws a new version has finished, because that is when its pictures exist. */
export function refreshTemplateVersions(projectId: string) {
  return queryClient.fetchQuery({ ...templateVersionsQueries.ofProject(projectId), staleTime: 0 })
}

/**
 * `useGenerateVariation().mutateAsync({ projectId, ...body })`: starts one variation and answers with it and the task to follow. Several can be
 * started together (Generate all): each call is its own mutation result, so none waits for another. Never retried by itself.
 */
export function useGenerateVariation() {
  return useMutation<CreateTemplateVersionResponseDto, ApiError, GenerateVariationBody & { projectId: string }>({
    mutationFn: ({ projectId, ...body }) => templateVersionsService.generate(projectId, body),
    onSuccess: (_result, { projectId }) => queryClient.invalidateQueries({ queryKey: templateVersionsKeys.all({ projectId }) }),
  })
}

/** Every cached list of variations, changed in place (the lists are plain arrays of versions). */
function patchLists(change: (list: TemplateVersionDto[]) => TemplateVersionDto[]) {
  queryClient.setQueriesData<TemplateVersionDto[]>({ queryKey: templateVersionsKeys.root }, (list) => (Array.isArray(list) ? change(list) : list))
}

/**
 * `useUpdateTemplateVersion().mutateAsync({ id, body })`: renames, shares for review or approves one variation. The server's answer replaces
 * the version in every list at once. Never retried by itself.
 */
export function useUpdateTemplateVersion() {
  return useMutation<TemplateVersionDto, ApiError, { id: string; body: TemplateVersionUpdateBody }>({
    mutationFn: ({ id, body }) => templateVersionsService.update(id, body),
    onSuccess: (version) => patchLists((list) => list.map((item) => (item.id === version.id ? { ...item, ...version, artworkUrls: version.artworkUrls.length > 0 ? version.artworkUrls : item.artworkUrls } : item))),
  })
}

/** `useDeleteTemplateVersion().mutateAsync(id)`: the variation leaves every list once the server has deleted it. */
export function useDeleteTemplateVersion() {
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => templateVersionsService.remove(id),
    onSuccess: (_result, id) => patchLists((list) => list.filter((item) => item.id !== id)),
  })
}
